import React, { useState, useCallback, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { X } from 'lucide-react'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import InputField from 'components/styledFormElements/InputField'
import { getImageSrc, getRelativeIconPath } from 'services/util'
import { Question } from './types'
import { fetchCategorySearchApi } from 'hooks/category-manage'
import { fetchCollectionSearchApi } from 'hooks/collection'
import { useDebounce } from 'hooks/useDebounce'

interface QuestionFormData {
    level: 'Level-1' | 'Level-2'
    parent_option: number | null
    option_title: string
    chip_image: string
    status: string
    categories: number[] | null
}

interface FormModalProps {
    isOpen: boolean
    onClose: () => void
    editingQuestion: Question | null
    parentQuestion: Question | null
    level1Questions: Question[]
    onSuccess: () => void
    createQuestion: any
    updateQuestion: any
    notification: any
}

const FormModal: React.FC<FormModalProps> = ({
    isOpen,
    onClose,
    editingQuestion,
    parentQuestion,
    level1Questions,
    onSuccess,
    createQuestion,
    updateQuestion,
    notification
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [categorySearch, setCategorySearch] = useState('')
    const [categoryOptions, setCategoryOptions] = useState<any[]>([])
    const [categoryLoading, setCategoryLoading] = useState(false)
    const [selectedCategoryObjects, setSelectedCategoryObjects] = useState<any[]>([])
    const [collectionSearch, setCollectionSearch] = useState('')
    const [collectionOptions, setCollectionOptions] = useState<any[]>([])
    const [collectionLoading, setCollectionLoading] = useState(false)
    const [selectedCollectionObjects, setSelectedCollectionObjects] = useState<any[]>([])
    const debouncedCategorySearch = useDebounce(categorySearch, 400)
    const debouncedCollectionSearch = useDebounce(collectionSearch, 400)

    const { register, handleSubmit, reset, control, clearErrors, formState: { errors } } = useForm<QuestionFormData>({
        defaultValues: {
            level: 'Level-1',
            parent_option: null,
            option_title: '',
            chip_image: '',
            status: 'active',
            categories: null
        }
    })

    // Fetch categories on search
    useEffect(() => {
        if (!debouncedCategorySearch.trim()) {
            setCategoryOptions([])
            return
        }

        const loadCategories = async () => {
            try {
                setCategoryLoading(true)
                const results = await fetchCategorySearchApi(debouncedCategorySearch)
                setCategoryOptions(results || [])
            } catch (error) {
                console.error('Error fetching categories:', error)
                setCategoryOptions([])
            } finally {
                setCategoryLoading(false)
            }
        }

        loadCategories()
    }, [debouncedCategorySearch])

    // Fetch collections on search
    useEffect(() => {
        if (!debouncedCollectionSearch.trim()) {
            setCollectionOptions([])
            return
        }

        const loadCollections = async () => {
            try {
                setCollectionLoading(true)
                const results = await fetchCollectionSearchApi(debouncedCollectionSearch)
                setCollectionOptions(results || [])
            } catch (error) {
                console.error('Error fetching collections:', error)
                setCollectionOptions([])
            } finally {
                setCollectionLoading(false)
            }
        }

        loadCollections()
    }, [debouncedCollectionSearch])

    // Fetch category and collection details when editing a question with entities
    useEffect(() => {
        if (isOpen && editingQuestion && editingQuestion.entity && editingQuestion.entity.length > 0) {
            const categoryItems = editingQuestion.entity.filter(item => item.item_type === 'CATEGORY')
            const collectionItems = editingQuestion.entity.filter(item => item.item_type === 'COLLECTION')

            setSelectedCategoryObjects(categoryItems.map(item => ({
                id: item.item_id,
                name: item.item_name
            })))
            setSelectedCollectionObjects(collectionItems.map(item => ({
                id: item.item_id,
                name: item.item_name
            })))
        }
    }, [isOpen, editingQuestion])

    // Reset form when editingQuestion or parentQuestion changes
    useEffect(() => {
        if (isOpen) {
            if (editingQuestion) {
                reset({
                    level: editingQuestion.level as 'Level-1' | 'Level-2',
                    parent_option: editingQuestion.parent_option,
                    option_title: editingQuestion.option_title,
                    chip_image: editingQuestion.chip_image || '',
                    status: editingQuestion.status,
                    categories: editingQuestion.categories || null
                })
                // Don't set selectedCategoryObjects here - it's handled in the effect above
                if (!editingQuestion.entity || editingQuestion.entity.length === 0) {
                    setSelectedCategoryObjects([])
                    setSelectedCollectionObjects([])
                }
            } else if (parentQuestion) {
                reset({
                    level: 'Level-2',
                    parent_option: parentQuestion.id,
                    option_title: '',
                    chip_image: '',
                    status: 'active',
                    categories: null
                })
                setSelectedCategoryObjects([])
                setSelectedCollectionObjects([])
            } else {
                reset({
                    level: 'Level-1',
                    parent_option: null,
                    option_title: '',
                    chip_image: '',
                    status: 'active',
                    categories: null
                })
                setSelectedCategoryObjects([])
                setSelectedCollectionObjects([])
            }
            setCategorySearch('')
            setCollectionSearch('')
        }
    }, [isOpen, editingQuestion, parentQuestion, reset])

    const onSubmit = useCallback(async (data: QuestionFormData) => {
        setIsSubmitting(true)

        try {
            const chipImagePath = data.chip_image ? getRelativeIconPath(data.chip_image) : null

            const payload: any = {
                level: data.level,
                option_title: data.option_title,
                chip_image: chipImagePath,
                status: data.status,
                ...(data.level === 'Level-2' && { parent_option: data.parent_option }),
            }

            payload.entity = [
                ...selectedCategoryObjects.map(cat => ({
                        item_type: 'CATEGORY',
                        item_id: cat.id,
                        item_name: cat.name
                })),
                ...selectedCollectionObjects.map(collection => ({
                    item_type: 'COLLECTION',
                    item_id: collection.id,
                    item_name: collection.name
                }))
            ]

            if (editingQuestion) {
                await updateQuestion.mutateAsync({
                    data: payload,
                    questionId: editingQuestion.id,
                })
                notification.push({
                    title: 'Success',
                    type: 'success',
                    message: 'Question updated successfully',
                })
            } else {
                await createQuestion.mutateAsync({ data: payload })
                notification.push({
                    title: 'Success',
                    type: 'success',
                    message: 'Question created successfully',
                })
            }

            onSuccess()
        } catch (error: any) {
            notification.push({
                title: 'Error',
                type: 'error',
                message: error?.response?.data?.message || `Failed to ${editingQuestion ? 'update' : 'create'} question`,
            })
        } finally {
            setIsSubmitting(false)
        }
    }, [editingQuestion, createQuestion, updateQuestion, notification, onSuccess, selectedCategoryObjects, selectedCollectionObjects])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {editingQuestion ? 'Edit Question' : parentQuestion ? `Add Child Question to "${parentQuestion.option_title}"` : 'Add New Question'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="float-right bg-blue-100 dark:bg-slate-700 dark:text-white text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 font-medium rounded-full text-sm p-1 text-center inline-flex items-center"
                    >
                        <XMarkIcon className="float-right h-4 w-4" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Level */}
                            <div className="flex flex-col w-full">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Level <span className="text-red-700">*</span>
                                </label>
                                <Controller
                                    name="level"
                                    control={control}
                                    rules={{ required: 'Level is required' }}
                                    render={({ field }) => (
                                        <select
                                            {...field}
                                            disabled={!!editingQuestion || !!parentQuestion}
                                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white disabled:opacity-50"
                                        >
                                            <option value="Level-1">Level-1</option>
                                            <option value="Level-2">Level-2</option>
                                        </select>
                                    )}
                                />
                                {errors.level && (
                                    <p className="text-xs text-red-600 mt-1">{errors.level.message}</p>
                                )}
                            </div>

                            {/* Parent Question (only for Level-2) */}
                            <Controller
                                name="level"
                                control={control}
                                render={({ field: levelField }) => (
                                    <>
                                        {levelField.value === 'Level-2' && (
                                            <div className="flex flex-col w-full">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Parent Question
                                                </label>
                                                <Controller
                                                    name="parent_option"
                                                    control={control}
                                                    // rules={{ required: levelField.value === 'Level-2' ? 'Parent question is required' : false }}
                                                    render={({ field }) => (
                                                        <select
                                                            {...field}
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                                                            disabled={!!editingQuestion || !!parentQuestion}
                                                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white disabled:opacity-50"
                                                        >
                                                            <option value="">Select Parent</option>
                                                            {level1Questions.map((q) => (
                                                                <option key={q.id} value={q.id}>{q.option_title}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                />
                                                {errors.parent_option && (
                                                    <p className="text-xs text-red-600 mt-1">{errors.parent_option.message}</p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            />
                        </div>

                        {/* Category Dropdown */}
                        <div className="flex flex-col w-full">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Categories
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={categorySearch}
                                    onChange={(e) => setCategorySearch(e.target.value)}
                                    placeholder="Search categories..."
                                    className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                                />
                                {categorySearch && (
                                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:bg-slate-800 dark:border-slate-600">
                                        {categoryLoading && (
                                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                                Searching...
                                            </div>
                                        )}
                                        {!categoryLoading && categoryOptions.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                                No results found
                                            </div>
                                        )}
                                        {categoryOptions.map((category) => {
                                            const isSelected = selectedCategoryObjects.some(cat => cat.id === category.id)
                                            return (
                                                <div
                                                    key={category.id}
                                                    onClick={() => {
                                                        if (!isSelected) {
                                                            setSelectedCategoryObjects([...selectedCategoryObjects, category])
                                                        }
                                                        setCategorySearch('')
                                                    }}
                                                    className={`cursor-pointer px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-slate-700 dark:text-white ${
                                                        isSelected ? 'bg-gray-100 dark:bg-slate-700 opacity-50' : ''
                                                    }`}
                                                >
                                                    {category.name}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                            {selectedCategoryObjects.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedCategoryObjects.map((category) => (
                                        <div
                                            key={category.id}
                                            className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-md text-sm"
                                        >
                                            <span>{category.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedCategoryObjects(selectedCategoryObjects.filter(c => c.id !== category.id))
                                                }}
                                                className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Collection Dropdown */}
                        <div className="flex flex-col w-full">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Collections
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={collectionSearch}
                                    onChange={(e) => setCollectionSearch(e.target.value)}
                                    placeholder="Search collections..."
                                    className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                                />
                                {collectionSearch && (
                                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:bg-slate-800 dark:border-slate-600">
                                        {collectionLoading && (
                                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                                Searching...
                                            </div>
                                        )}
                                        {!collectionLoading && collectionOptions.length === 0 && (
                                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                                No results found
                                            </div>
                                        )}
                                        {collectionOptions.map((collection) => {
                                            const isSelected = selectedCollectionObjects.some(item => item.id === collection.id)
                                            return (
                                                <div
                                                    key={collection.id}
                                                    onClick={() => {
                                                        if (!isSelected) {
                                                            setSelectedCollectionObjects([...selectedCollectionObjects, collection])
                                                        }
                                                        setCollectionSearch('')
                                                    }}
                                                    className={`cursor-pointer px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-slate-700 dark:text-white ${
                                                        isSelected ? 'bg-gray-100 dark:bg-slate-700 opacity-50' : ''
                                                    }`}
                                                >
                                                    {collection.name}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                            {selectedCollectionObjects.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {selectedCollectionObjects.map((collection) => (
                                        <div
                                            key={collection.id}
                                            className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-md text-sm"
                                        >
                                            <span>{collection.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedCollectionObjects(selectedCollectionObjects.filter(item => item.id !== collection.id))
                                                }}
                                                className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Question Title */}
                        <div>
                            <InputField
                                label="Question Title"
                                name="option_title"
                                type="text"
                                register={register('option_title', {
                                    required: 'Question title is required',
                                    maxLength: {
                                        value: 500,
                                        message: 'Title must be less than 500 characters'
                                    }
                                })}
                                error={errors.option_title?.message}
                                placeholder="Enter question title..."
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Chip Icon */}
                            <div className="flex flex-col w-full">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Chip Icon
                                </label>
                                <Controller
                                    name="chip_image"
                                    control={control}
                                    render={({ field }) => {
                                        const iconValue = field.value

                                        return (
                                            <div className="relative">
                                                {iconValue ? (
                                                    <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                        <div className="flex items-center gap-2">
                                                            <img
                                                                src={
                                                                    iconValue.startsWith('http')
                                                                        ? iconValue
                                                                        : getImageSrc(iconValue)
                                                                }
                                                                alt="Selected icon"
                                                                className="w-6 h-6 object-contain"
                                                            />
                                                            <span className="text-sm text-gray-700 dark:text-gray-200">
                                                                Icon selected
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                field.onChange('')
                                                                clearErrors('chip_image')
                                                            }}
                                                            className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <IconDropdown
                                                        value={iconValue}
                                                        onChange={(selectedIcon: any) => {
                                                            const iconKey =
                                                                selectedIcon?.path ||
                                                                selectedIcon?.image ||
                                                                selectedIcon?.icon ||
                                                                ''
                                                            field.onChange(iconKey)
                                                            if (iconKey) {
                                                                clearErrors('chip_image')
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )
                                    }}
                                />
                            </div>

                            {/* Status */}
                            <div className="flex flex-col w-full">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Status <span className="text-red-700">*</span>
                                </label>
                                <Controller
                                    name="status"
                                    control={control}
                                    rules={{ required: 'Status is required' }}
                                    render={({ field }) => (
                                        <select
                                            {...field}
                                            className="w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    )}
                                />
                                {errors.status && (
                                    <p className="text-xs text-red-600 mt-1">{errors.status.message}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-slate-700 dark:text-gray-200 dark:border-slate-600 dark:hover:bg-slate-600 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || createQuestion.isLoading || updateQuestion.isLoading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSubmitting || createQuestion.isLoading || updateQuestion.isLoading 
                                ? 'Saving...' 
                                : editingQuestion ? 'Update Question' : 'Create Question'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default FormModal
